import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { convertDurationFromString, openUrlInNewTab } from '../../../../../models/UiUtils';
import { invalidateMyTemplates, useContactCustomFields, useMyTemplatesQuery } from '../../../../../queries';
import { useAccountAvailableOAuthProviders } from '../../../../../queries/Account/useAccountAvailableOAuthProviders';
import { AccountDetailsContext, AccountDetailsSettingsContext } from '../../../../../viewmodels/AdminViewModels';
import {
	AutomationTriggerType,
	ContactOwnerCriteria,
	ETextingFeatureProvider,
	HandwrittenCardProvider,
	IActionableResourceSelectorSettings,
	IHandwrittenCardTemplate,
	INewPolicyActionableResourceSelectorSettings,
	IImpersonationContext,
	IMatterDataboardTagSettings,
	Industry,
	IPolicyDataboardTagSettings,
	isDevOrTestEnvironment,
	ResourceSelectorId,
	TemplateType,
	UpcomingKeyDateDisplayScope,
} from '../../../../../viewmodels/AppViewModels';
import { ConfirmationDialog } from '../../../../../web/components/ConfirmationDialog';
import { ISelectOption } from '../../../../../web/components/DeprecatedSelect';
import { EditItemPenIcon } from '../../../../../web/components/svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { actionItemTintColor } from '../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { AddCompanyLogoModal } from '../../../../components/AddCompanyLogoModal';
import { CreateCustomDashboardCardModal, ISubmittedValues } from '../../../../components/CreateCustomDashboardCard';
import { CreateCustomFieldsModal } from '../../../../components/CreateCustomFieldsModal';
import { ExportDataButton, ExportDataType } from '../../AccountDetails/ExportDataButton';
import {
	ComplianceWaitTimeOptions,
	ContactOwnerCriteriaOptions,
	CustomCardDaysOptions,
	DefaultEmailFontFaceOptions,
	DefaultEmailFontSizeOptions,
	DeleteKeyFactsConfirmationOptions,
	EnhancementOptions,
	EnhancementType,
	HandwrittenCardProviderOptions,
	MaximumGroupAutomationContacts,
	MaximumGroupSendContacts,
	MaximumHTMLNewsletterContacts,
	Privacy,
	PrivacyOptions,
	RenewalDaysOptions,
	RenewalMonthsOptions,
	RoleForOpportunitiesOptions,
	SendOnBehalfWaitTimeOptions,
	TextingProviderOptions,
	Turning65Options,
	Turning72Options,
	Turning73Options,
	TurningXX3Options,
	TurningXX4Options,
	TurningXX5Options,
	TurningXXOptions,
	UpcomingKeyDateDisplayScopes,
	UserOptions,
	UserRole,
	valueAsContextAndTextOption,
} from './options';
import { Setting, SettingCategory, SettingType } from './Setting';
import { CustomLabel } from './Setting/CustomLabel';
import { styleSheet } from './styles';

interface IProps {
	canModifyAccount?: boolean;
	category?: SettingCategory;
	hasDevPermission?: boolean;
	hasSupportPermission?: boolean;
	settingsSearch?: string;
	onClearAllDonations?: () => void;
	onOpenDonationTagSettings?: () => void;
}

export enum SettingName {
	AddCustomCard = '+ Add Custom Card',
	AddCustomDashboardCard = '+ Add Custom Dashboard Card',
	AddCustomField = '+ Add Custom Field',
	AllowCompanyNameRequired = 'Allow company name to be required',
	AllowCsmToViewCampaign = 'Allow CSM to view "My Campaigns"',
	AllowTextingInTagAutomations = 'Allow Texting in Tag Automations',
	AllowTextingInManualAutomations = 'Allow Texting in Manually Triggered Automations',
	AllowToStartOnBehalf = "Start on Employee's Behalf",
	AllowStartAutomationsOnHold = 'Allow Users to Start Automations as Pending',
	AllowChangeUserEmail = 'Allow user email change',
	AnyUserCanEditNotes = 'Any User Can Edit Notes',
	Automations = 'Automations',
	BlogEnabled = 'Blogs',
	CarrierTags = 'Carrier Tags',
	CcEmployeeDuringSend = 'CC employee during a campaign send',
	ConnectionTypes = 'Connection Types',
	ContentGeneration = 'Content Generation',
	ComplianceEnabled = 'Compliance enabled for emails, social and blogs',
	SendComplianceReviewResultEmails = 'Send compliance confirmation emails',
	CreateContactsFromCalendar = 'Create Contacts From Calendar',
	CreateContactsFromSentMessages = 'Create Contacts From Sent Emails',
	CrossAccountTemplateSharing = 'Enable Cross Account Template Sharing',
	CustomContentCalendar = 'Enable Custom Content Calendar',
	CustomFields = 'Custom Fields',
	DataBoards = 'Data Boards',
	DataBoardDonations = 'Donations',
	ArchiveAllDonations = 'Archive All Donations',
	DonationTagging = 'Donation Tagging',
	DataBoardPolicies = 'Policies',
	DataBoardMatters = 'Matter Boards',
	DataBoardOpportunities = 'Opportunities',
	MatterStageTags = 'Matter Stage Tags',
	DefaultContactVisibility = 'Default Contact Visibility',
	DefaultUserRole = 'Default User Role',
	DefaultEmailStyle = 'Default Email Style',
	DefaultEmailFontFace = 'Font Face',
	DefaultEmailFontSize = 'Font Size',
	DeleteRenewalKeyFacts = 'Delete Renewal Key Facts',
	DigestEmails = 'Digest Emails',
	DisableMobileApps = 'Disable Mobile Apps',
	ExportNotes = 'Export Notes',
	ExportPosts = 'Export Social Media Posts',
	ExportTexts = 'Export Text Messages',
	HideCompanies = 'Hide Companies Tab',
	HideEmailCampaignSignature = 'Do not embed images in signatures as an attachment',
	ShowAllContactsByDefault = 'Send to All contacts by default',
	ShowInternalSendOption = 'Show Internal Send Option',
	EditNewsletterSettings = 'Manage Newsletter Settings',
	HandwrittenCards = 'Handwritten Cards',
	HIPAA = 'HIPAA',
	Households = 'Households',
	HouseholdsSendToHouseholdsByDefault = 'Send to households by default',
	HTMLNewsletters = 'HTML Newsletters',
	ImportFromProviderEnabled = 'Import Contacts From Email Provider',
	RequiredRoleToViewAllOpportunities = 'Required Role to view all Opportunities',
	RequiredRoleForFullLevitateUI = 'Required Role to view full Levitate',
	LineOfBusinessTags = 'Line of Business Tags',
	LogScannedEmails = 'Log Scanned Emails',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	MaximumGroupAutomationContacts = 'Maximum Group Automation Contacts',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	MaximumGroupSendContacts = 'Maximum Group Send Contacts',
	DailyEmailMaximum = 'Maximum Daily Email Sends',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	MaximumHTMLNewsletterContacts = 'Maximum HTML Newsletter Contacts',
	PublicAPI = 'Public API',
	ProfileEnrichment = 'Profile Enrichment',
	ResetAccount = 'Reset Account',
	SaveEmailAsNoteDefault = 'Save Email As Note Default',
	ScanEmailForLeads = 'Scan Email for Leads',
	ScheduleAMeeting = 'Schedule a Meeting',
	SendBdayEmail = 'Send Birthday Email',
	SendBdayFinancialReviewEmail = 'Send Bday/Financial Review Email',
	SendBdayRenewalEmail = 'Send Bday/Renewal Email',
	SendCampaignsOnBehalf = "Send Campaigns on Employee's Behalf",
	SendEmailForNewLeads = 'Send Email for New Leads',
	ShowDuplicateContacts = 'Show Duplicate Contacts',
	AllowEmailMergeIntoIntegration = 'Merge duplicate contacts by email into integration contacts',
	ShowInternalMeetings = 'Show Internal Meetings',
	ShowRenewalDashboardCards = 'Show Renewal Dashboard Cards',
	ShowTurningXXDashboardCard = 'Show Turning XX Dashboard Card',
	ShowTurningXX2DashboardCard = 'Show Turning XX2 Dashboard Card',
	ShowTurningXX3DashboardCard = 'Show Turning XX3 Dashboard Card',
	ShowTurningXX4DashboardCard = 'Show Turning XX4 Dashboard Card',
	ShowTurningXX5DashboardCard = 'Show Turning XX5 Dashboard Card',
	ShowTurning65DashboardCards = 'Show Turning 65 Dashboard Cards',
	ShowTurning72DashboardCards = 'Show Turning 72 Dashboard Cards',
	ShowTurning73DashboardCards = 'Show Turning 73 Dashboard Cards',
	ShowNewDonationsDashboardCard = 'Show New Donation Dashboard Card',
	ShowNewPolicyDashboardCard = 'Show New Policy Dashboard Card',
	ShowNewMatterDashboardCard = 'Show New Matter Dashboard Card',
	ShowMatterStageChangedDashboardCard = 'Show Matter Stage Changed Dashboard Card',
	ShowMatterStatusChangedDashboardCard = 'Show Matter Status Changed Dashboard Card',
	ShowFinancialReviewDashboardCards = 'Show Financial Review Dashboard Cards',
	ShowUpcomingBirthdaysTo = 'Show Upcoming Birthdays to',
	SmartSalutationsEnabled = 'Smart Salutations Enabled',
	SocialMedia = 'Social Media',
	SocialMediaLogoOverlay = 'Always overlay logo by default',
	SocialMediaHideBackground = 'Hide Background',
	SocialReadOnly = 'Read Only',
	StoreSubjectInCommunicationHistory = 'Store subjects in Communication History',
	SuppressContactsRemovedFromKeyDateSends = 'Suppress Contacts Removed from Key Date Sends',
	SuppressContactsRemovedFromRenewal = 'Suppress Contacts Removed from Renewal',
	Surveys = 'Surveys',
	SurveysDefaultReviewLink = 'Default review link',
	NewReviewsEmailNotification = 'Enable Notifications for new reviews',
	Texting = 'Texting',
	TextingArchivingEnabled = 'Archiving Enabled',
	UseParentTextingCampaignRegistration = 'Use Parent Texting Campaign Registration',
	TextingCampaignAutomationsTrigger = 'Texting Campaign Automation Trigger',
	TextingDailySendLimit = 'Daily Send Limit',
	TextingHourlySendLimit = 'Hourly Send Limit',
	TextingSecondsBetweenSends = 'Seconds Between Sends',
	Timeline = 'Timeline',
	TrackClickedEmails = 'Track Clicked Emails',
	TrackOpenedEmails = 'Track Opened Emails',
	Translations = 'Translations',
	TwoFactorAuthentication = 'Two Factor Authentication',
	AllowOAuthLogin = 'Allow OAuth Login',
	OnlyAllowOAuthLogin = 'Only Allow OAuth Login',
	EnabledOAuthProviders = 'Enabled OAuth Providers',
	WaitTimeForCompliance = 'Wait Time for Compliance Email Send',
	WaitTimeForSendOnBehalf = 'Wait Time for Send on Behalf',
	Websites = 'Websites',
	WebsitesDudaSiteId = 'Duda Site Id',
	WebsitesWebsiteUrl = 'Website URL',
	WeeklyTaggingGameReminder = 'Weekly Tagging Game Reminder',
	UseUserBccEmailAlwaysEnabled = 'Use User Bcc Email Always',
	PreApproved = 'Pre-Approved',
	PreReviewed = 'Pre-Reviewed',
	Rejected = 'Rejected',
	BrokerDealer = 'Broker Dealer',
	EnableStripePayments = 'Enable Stripe Payments',
	HideContactCompanyEmployeeReport = 'Hide Contact/Company/Employee Report',
}

export type TCustomSettings =
	| ResourceSelectorId.Custom1
	| ResourceSelectorId.Custom2
	| ResourceSelectorId.Custom3
	| ResourceSelectorId.Custom4
	| ResourceSelectorId.Custom5
	| ResourceSelectorId.Custom6
	| ResourceSelectorId.Custom7
	| ResourceSelectorId.Custom8
	| ResourceSelectorId.Custom9
	| ResourceSelectorId.Custom10;

export const SettingsColumns: React.FC<IProps> = props => {
	const [editCustomCard, setEditCustomCard] = useState<TCustomSettings | null>(null);
	const {
		category,
		canModifyAccount,
		hasDevPermission,
		hasSupportPermission,
		settingsSearch,
		onClearAllDonations,
		onOpenDonationTagSettings,
	} = props;
	const { account } = React.useContext(AccountDetailsContext);
	const turning65DaysToLookAhead = account?.preferences?.resourceSelectorSettings?.Turning65?.daysToLookAhead;
	const customCards =
		(Object.keys(account?.preferences?.resourceSelectorSettings ?? {})?.filter(key =>
			key.startsWith('Custom')
		) as TCustomSettings[]) || [];

	const impersonationContext: IImpersonationContext = React.useMemo(() => {
		return { account: { id: account.id } };
	}, [account.id]);

	const myHandwrittenCardTemplatesQuery = useMyTemplatesQuery<IHandwrittenCardTemplate>({
		enabled: true,
		impersonationContext,
		industry: account?.additionalInfo?.industry,
		templateType: TemplateType.HandwrittenCard,
	});
	const onRequestClose = () => {
		invalidateMyTemplates({ templateType: TemplateType.HandwrittenCard });
		setShowAddLogoModal(false);
	};
	const customFieldsQuery = useContactCustomFields({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: Boolean(account.additionalInfo?.hasCustomContactFields),
	});
	const customFields =
		Boolean(account.additionalInfo?.hasCustomContactFields) && customFieldsQuery.data?.fields
			? customFieldsQuery.data?.fields
			: [];

	const availableOAuthProvidersQuery = useAccountAvailableOAuthProviders({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: Boolean(account.oAuthLoginSettings?.allowOAuthLogin),
	});

	const oAuthProviderOptions: ISelectOption<string>[] = useMemo(() => {
		return (availableOAuthProvidersQuery.data || []).map(provider => ({
			dataContext: provider,
			id: `oauth-provider-${provider}`,
			text: provider,
			type: 'checkbox' as const,
		}));
	}, [availableOAuthProvidersQuery.data]);

	const selectedOAuthProviders: ISelectOption<string>[] = useMemo(() => {
		return oAuthProviderOptions.filter(option =>
			account?.oAuthLoginSettings?.enabledProviders?.includes(option.dataContext)
		);
	}, [oAuthProviderOptions, account?.oAuthLoginSettings?.enabledProviders]);

	const [showAddLogoModal, setShowAddLogoModal] = useState(false);
	const [showCreateCustomFieldsModal, setShowCreateCustomFieldsModal] = useState(false);
	const [showCreateCustomDashboardCardModal, setShowCreateCustomDashboardCardModal] = useState(false);
	const [confirmDisable, setConfirmDisable] = useState(false);

	const isDevOrSupport = useMemo(() => {
		return hasDevPermission || hasSupportPermission;
	}, [hasDevPermission, hasSupportPermission]);

	const {
		onAddCustomCard,
		onAddCustomField,
		onAdminSendOnBehalfContactOwnerCriteriaChanged,
		onBlogEnabledChanged,
		onClassificationEmailsChanged,
		onComplianceEnabledChanged,
		onSendComplianceReviewResultEmailsChanged,
		onComplianceWaitTimeChanged,
		onConnectionTypesEnabledChanged,
		onCrossAccountTemplateSharingChanged,
		onCreateContactsFromCalendarChanged,
		onCsmViewMyCampaignsEnabledChanged,
		onDefaultContactsVisibilityChanged,
		onDefaultUserRoleChanged,
		onDefaultEmailStyleEnabledChanged,
		onDefaultEmailStyleFontFaceChanged,
		onDefaultEmailStyleFontSizeChanged,
		onDeleteRenewalKeyFactsChanged,
		onDisableMobileAppsChanged,
		onHideCompaniesChanged,
		onHideContactCompanyEmployeeReportChanged,
		onHIPAAEnabledChanged,
		onHideEmailCampaignSignatureChanged,
		onShowAllContactsByDefaultChanged,
		onEditHtmlNewsletterFooter,
		onEnableCcInCampaignsChanged,
		onEnableContentGenerationChanged,
		onEnableCustomContentCalendarChanged,
		onEnablePublicAPIChanged,
		onEnableStripePaymentsChanged,
		onEnableSocialMediaChanged,
		onEnableSocialMediaLogoOverlayChanged,
		onEnableSocialMediaLogoBackgroundChanged,
		onEnableSocialMediaReadOnly,
		onEnableSurveysChanged,
		onEnableTranslationChanged,
		onHandwrittenCardsEnabledChanged,
		onHandwrittenCardsProviderChanged,
		onHouseholdsEnabledChanged,
		onHouseholdsSendToHouseholdByDefaultChanged,
		onHtmlNewsletterChanged,
		onInternalMeetingsChanged,
		onLookForLeadsChanged,
		onMeetingCompanyNameRequiredChanged,
		onMeetingSchedulerEnabled,
		onDataBoardOpportunitiesEnabledChanged,
		onDataBoardDonationsEnabledChanged,
		onDataBoardPoliciesCarrierTagsEnabledChanged,
		onDataBoardPoliciesLineOfBusinessTagsEnabledChanged,
		onDataBoardPoliciesEnabledChanged,
		onDataBoardMattersEnabledChanged,
		onDataBoardMattersMatterStageTagsEnabledChanged,
		onRequiredRoleToViewAllOpportunities,
		onRequiredRoleForFullLevitateUI,
		onResetAccountClicked,
		onSaveForAnalysisChanged,
		onSendEmailForNewLeadChanged,
		onSendOnBehalfEnabled: onSendOnBehalfChanged,
		onSendOnBehalfWaitTimeChanged,
		onSentMailClickTrackingChanged,
		onSentMailOpenTrackingChanged,
		onSetShowInternalSendOption,
		onSetAllowStartAutomationsOnHold,
		onSetEnhancementOption,
		onSetFinancialReviewOption,
		onSetFinancialReviewOptionChange,
		onSetMaximumGroupAutomationContacts,
		onSetMaximumGroupSendContacts,
		onSetMaximumHTMLNewsletterContacts,
		onSetNoteViewerCanEditEnabledChanged,
		onSetRenewalReminderDaysOption,
		onSetRenewalReminderDaysOptionChange,
		onSetRenewalSuppressMonthsOption,
		onSetRenewalSuppressMonthsOptionChange,
		onSetShowUpcomingBirthdaysToOption: onSetShowUpcomingBirthdaysTo,
		onToggleAccountPreferenceBoolean,
		onSetSuppressContactsRemovedFromResourceSends,
		onSetSurveyDefaultReviewLink,
		onSetSurveyNewReviewsEmailNotification,
		onSetTurning65MonthsOption,
		onSetTurning65MonthsOptionChange,
		onSetTurning72MonthsOption,
		onSetTurning72MonthsOptionChange,
		onSetTurning73MonthsOption,
		onSetTurning73MonthsOptionChange,
		onSetNewDonationsOptionChange,
		onSetNewPolicyOptionChange,
		onSetNewMatterOptionChange,
		onSetMatterStageChangedOptionChange,
		onSetMatterStatusChangedOptionChange,
		onSetTurningXXMonthsOption,
		onSetTurningXXMonthsOptionChange,
		onSetTurningXX2MonthsOption,
		onSetTurningXX2MonthsOptionChange,
		onTurningXX2InputChanged,
		onSetTurningXX3MonthsOption,
		onSetTurningXX3MonthsOptionChange,
		onTurningXX3InputChanged,
		onSetTurningXX4MonthsOption,
		onSetTurningXX4MonthsOptionChange,
		onTurningXX4InputChanged,
		onSetTurningXX5MonthsOption,
		onSetTurningXX5MonthsOptionChange,
		onTurningXX5InputChanged,
		onSuppressCustomCardOptionChange,
		onTextingChanged,
		onTextingDailySendLimitChanged,
		onTextingHourlySendLimitChanged,
		onTextingProviderChanged,
		onTextingSecondsBetweenSendsChanged,
		onTextingRequireArchivingChanged,
		onTextingUseParentCampaignRegistrationChanged,
		onTurningXXInputChanged,
		onTimelineChanged,
		onTwoFactorAuthChanged,
		onAllowOAuthLoginChanged,
		onOnlyAllowOAuthLoginChanged,
		onEnabledOAuthProvidersChanged,
		onWebsitesChanged,
		onWebsitesDudaSiteIdChanged,
		onWebsitesWebsiteUrlChanged,
		onUpdateCustomCard,
		onUpdateCustomCardDays,
		setStartOnBehalfTriggerEnabled,
		setTagAutomationTriggerEnabled,
		setManualAutomationTriggerEnabled,
		setTextingCampaignAutomationTriggerEnabled,
		toggleAutomations,
		onUpdatePreApprovedFeature,
		onUpdateRejectedFeature,
		onUpdatePreApprovedDisplayText,
		onUpdatePreApprovedEmailPrefix,
		onUpdatePreReviewedFeature,
		onUpdatePreReviewedDisplayText,
		onUpdatePreReviewedEmailPrefix,
		onUpdateRejectedEmailPrefix,
		onUpdateRejectedDisplayText,
	} = React.useContext(AccountDetailsSettingsContext);

	const handleOAuthProvidersChange = useCallback(
		(selectedOptions: ISelectOption<string>[]) => {
			const providers = selectedOptions.map(option => option.dataContext);
			onEnabledOAuthProvidersChanged?.(providers);
		},
		[onEnabledOAuthProvidersChanged]
	);

	const surveyDefaultReviewUrl = account?.features?.surveys?.defaultReviewLink;
	const [pendingSurveysDefaultReviewUrl, setPendingSurveysDefaultReviewUrl] = useState<string>(
		surveyDefaultReviewUrl || ''
	);

	const [websitesWebsiteUrl, setWebsitesWebsiteUrl] = useState<string>(account?.features?.websites?.websiteUrl || '');

	const [websitesDudaSiteId, setWebsitesDudaSiteId] = useState<string>(account?.features?.websites?.dudaSiteId || '');

	const [pendingPreApprovedDisplayText, setPendingPreApprovedDisplayText] = useState<string>(
		account?.features?.templateReviewSettings?.preApproved?.displayText || ''
	);
	const [pendingPreApprovedEmailPrefix, setPendingPreApprovedEmailPrefix] = useState<string>(
		account?.features?.templateReviewSettings?.preApproved?.complianceEmailSubjectPrefix || ''
	);
	const [pendingPreReviewedDisplayText, setPendingPreReviewedDisplayText] = useState<string>(
		account?.features?.templateReviewSettings?.preReviewed?.displayText || ''
	);
	const [pendingPreReviewedEmailPrefix, setPendingPreReviewedEmailPrefix] = useState<string>(
		account?.features?.templateReviewSettings?.preReviewed?.complianceEmailSubjectPrefix || ''
	);
	const [pendingRejectedDisplayText, setPendingRejectedDisplayText] = useState<string>(
		account?.features.templateReviewSettings?.rejected?.displayText || ''
	);
	const [pendingRejectedEmailPrefix, setPendingRejectedEmailPrefix] = useState<string>(
		account?.features.templateReviewSettings?.rejected?.complianceEmailSubjectPrefix || ''
	);

	const onPreApprovedDisplayTextChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingPreApprovedDisplayText(e.target.value);
	}, []);

	const onPreApprovedEmailPrefixChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingPreApprovedEmailPrefix(e.target.value);
	}, []);

	const onPreReviewedDisplayTextChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingPreReviewedDisplayText(e.target.value);
	}, []);

	const onPreReviewedEmailPrefixChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingPreReviewedEmailPrefix(e.target.value);
	}, []);

	const onRejectedDisplayTextChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingRejectedDisplayText(e.target.value);
	}, []);

	const onRejectedEmailPrefixChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingRejectedEmailPrefix(e.target.value);
	}, []);

	const onShowModal = () => {
		setShowCreateCustomDashboardCardModal(true);
	};

	const onModalRequestClose = () => {
		setShowCreateCustomDashboardCardModal(false);
		setEditCustomCard(null);
	};

	const uploadLogo = () => {
		setShowAddLogoModal(true);
	};

	const editCustomFields = () => {
		setShowCreateCustomFieldsModal(true);
	};

	const createCustomFieldClicked = () => {
		setShowCreateCustomFieldsModal(true);
	};

	const getDisplayScope = useCallback(() => {
		const preferences = account?.preferences;
		if (preferences.showUpcomingBirthdaysTo === UpcomingKeyDateDisplayScope.OwnersOnly) {
			return UpcomingKeyDateDisplayScopes.find(x => x.dataContext === UpcomingKeyDateDisplayScope.OwnersOnly);
		} else if (preferences.showUpcomingBirthdaysTo === UpcomingKeyDateDisplayScope.Connections) {
			return UpcomingKeyDateDisplayScopes.find(x => x.dataContext === UpcomingKeyDateDisplayScope.Connections);
		}
	}, [account.preferences]);

	const getContactOwnerForResourceSelectorOptions = useCallback(
		(resourceSelectorId: ResourceSelectorId) => {
			const resourceSelectorSettings = account?.preferences?.resourceSelectorSettings;
			if (resourceSelectorSettings?.[resourceSelectorId]?.contactOwnerCriteria === ContactOwnerCriteria.ForUserOnly) {
				return ContactOwnerCriteriaOptions.find(x => x.dataContext === ContactOwnerCriteria.ForUserOnly);
			} else if (
				resourceSelectorSettings?.[resourceSelectorId]?.contactOwnerCriteria ===
				ContactOwnerCriteria.OnBehalfOfContactOwners
			) {
				return ContactOwnerCriteriaOptions.find(x => x.dataContext === ContactOwnerCriteria.OnBehalfOfContactOwners);
			} else {
				return ContactOwnerCriteriaOptions.find(x => x.dataContext === ContactOwnerCriteria.Default);
			}
		},
		[account?.preferences?.resourceSelectorSettings]
	);

	const getEnhancementSetting = useCallback(() => {
		const preferences = account?.preferences;
		if (preferences.enableCompanyEnhancement) {
			return EnhancementOptions.find(x => x.dataContext === EnhancementType.CompaniesOnly);
		} else if (!preferences.enableCompanyEnhancement) {
			return EnhancementOptions.find(x => x.dataContext === EnhancementType.Off);
		}
	}, [account?.preferences]);

	const getManuallySet65Option = useMemo(() => {
		const daysMatchOptions = Turning65Options.find(x => x.dataContext === turning65DaysToLookAhead);
		if (turning65DaysToLookAhead && !daysMatchOptions) {
			const manuallySet65Option: ISelectOption<number> = {
				dataContext: turning65DaysToLookAhead,
				id: 'turning-65-option-manual',
				text: `${turning65DaysToLookAhead} days`,
			};
			return manuallySet65Option;
		}
	}, [turning65DaysToLookAhead]);

	const allTurning65Options = useMemo(() => {
		return getManuallySet65Option ? [...Turning65Options, getManuallySet65Option] : Turning65Options;
	}, [getManuallySet65Option]);

	const getTurning65CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.Turning65?.daysToLookAhead;
		return allTurning65Options.find(x => x.dataContext === days) ?? Turning65Options.find(x => x.dataContext === 60);
	}, [account, allTurning65Options]);

	const getTurningXXCardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.TurningXX?.daysToLookAhead;
		return TurningXXOptions.find(x => x.dataContext === days) ?? TurningXXOptions.find(x => x.dataContext === 60);
	}, [account]);

	const getTurningXX2CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.TurningXX2?.daysToLookAhead;
		return TurningXXOptions.find(x => x.dataContext === days) ?? TurningXXOptions.find(x => x.dataContext === 60);
	}, [account]);

	const getTurningXX3CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.TurningXX3?.daysToLookAhead;
		return TurningXX3Options.find(x => x.dataContext === days) ?? TurningXX3Options.find(x => x.dataContext === 60);
	}, [account]);

	const getTurningXX4CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.TurningXX4?.daysToLookAhead;
		return TurningXX4Options.find(x => x.dataContext === days) ?? TurningXX4Options.find(x => x.dataContext === 60);
	}, [account]);

	const getTurningXX5CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.TurningXX5?.daysToLookAhead;
		return TurningXX5Options.find(x => x.dataContext === days) ?? TurningXX5Options.find(x => x.dataContext === 60);
	}, [account]);

	const getCustomCardsOption = useCallback(
		(card: TCustomSettings) => {
			const days = account?.preferences?.resourceSelectorSettings?.[card].daysToLookAhead || 60;
			return CustomCardDaysOptions.find(x => x.dataContext === days);
		},
		[account]
	);

	const getTurning72CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.Turning72?.daysToLookAhead;
		return Turning72Options.find(x => x.dataContext === days) ?? Turning72Options.find(x => x.dataContext === 60);
	}, [account?.preferences?.resourceSelectorSettings]);

	const getTurning73CardsOption = useCallback(() => {
		const days = account?.preferences?.resourceSelectorSettings?.Turning73?.daysToLookAhead;
		return Turning73Options.find(x => x.dataContext === days) ?? Turning73Options.find(x => x.dataContext === 60);
	}, [account?.preferences?.resourceSelectorSettings]);

	const getRenewalSuppressOption = useCallback(() => {
		const months = account?.preferences?.renewalEmailMonthsToSuppress;
		return (
			RenewalMonthsOptions.find(x => x.dataContext === months) ?? RenewalMonthsOptions.find(x => x.dataContext === 0)
		);
	}, [account?.preferences?.renewalEmailMonthsToSuppress]);

	const getRenewalCardsOption = useCallback(() => {
		const days = account?.preferences?.renewalEmailDaysToLookAhead;
		return RenewalDaysOptions.find(x => x.dataContext === days) ?? RenewalDaysOptions.find(x => x.dataContext === 0);
	}, [account?.preferences?.renewalEmailDaysToLookAhead]);

	const getFinancialReviewOption = () => {
		const days = account?.preferences?.resourceSelectorSettings?.FinancialReview?.daysToLookAhead;
		return RenewalDaysOptions.find(x => x.dataContext === days) ?? RenewalDaysOptions.find(x => x.dataContext === 60);
	};

	const getSendOnBehalfWaitTimeOption = useCallback(() => {
		const duration = !account?.preferences?.sendOnBehalfWaitTime
			? SendOnBehalfWaitTimeOptions[0].dataContext
			: convertDurationFromString(account?.preferences?.sendOnBehalfWaitTime);
		if (duration.days === 2) {
			return SendOnBehalfWaitTimeOptions.find(x => x.dataContext.days === duration.days);
		} else if (duration.days === 1) {
			return SendOnBehalfWaitTimeOptions.find(x => x.dataContext.days === duration.days);
		} else if (duration.hours === 1) {
			return SendOnBehalfWaitTimeOptions.find(x => x.dataContext.hours === duration.hours);
		} else {
			return SendOnBehalfWaitTimeOptions.find(x => x.dataContext === duration);
		}
	}, [account?.preferences?.sendOnBehalfWaitTime]);

	const getComplianceWaitTimeOption = useCallback(() => {
		const duration = !account?.preferences?.complianceSettings?.waitTime
			? ComplianceWaitTimeOptions[0].dataContext
			: convertDurationFromString(account?.preferences?.complianceSettings?.waitTime);
		if (duration.days === 2 || duration.days === 1) {
			return ComplianceWaitTimeOptions.find(x => x.dataContext.days === duration.days);
		} else if (duration.hours === 1) {
			return ComplianceWaitTimeOptions.find(x => x.dataContext.hours === duration.hours);
		} else {
			return ComplianceWaitTimeOptions.find(x => x.dataContext === duration);
		}
	}, [account?.preferences?.complianceSettings?.waitTime]);

	const getMaximumGroupAutomationContacts = useCallback(() => {
		const amount = account?.preferences?.maximumGroupAutomationContacts;
		return MaximumGroupAutomationContacts.find(x => x.dataContext === amount) || amount
			? valueAsContextAndTextOption('max-group-automation-contacts-current', amount)
			: MaximumGroupAutomationContacts[0];
	}, [account?.preferences?.maximumGroupAutomationContacts]);

	const getMaximumGroupSendContacts = useCallback(() => {
		const amount = account?.preferences?.maximumGroupSendContacts;
		return MaximumGroupSendContacts.find(x => x.dataContext === amount) || amount
			? valueAsContextAndTextOption('max-group-send-contacts-current', amount)
			: MaximumGroupSendContacts[0];
	}, [account?.preferences?.maximumGroupSendContacts]);

	const getMaximumHTMLNewsletterContacts = useCallback(() => {
		const amount = account?.features?.htmlNewsletter?.maximumContactsPerSend;
		return MaximumHTMLNewsletterContacts.find(x => x.dataContext === amount) || amount
			? valueAsContextAndTextOption('max-html-contacts-current', amount)
			: MaximumHTMLNewsletterContacts[0];
	}, [account?.features?.htmlNewsletter?.maximumContactsPerSend]);

	const getMatterBoardEnabled = useCallback(() => {
		return account?.features?.boards?.matterBoard?.enabled ?? false;
	}, [account?.features?.boards?.matterBoard?.enabled]);

	const getMatterBoardTagSettings = useCallback((): IMatterDataboardTagSettings | undefined => {
		return account?.features?.boards?.matterBoard?.configuration?.tagSettings as IMatterDataboardTagSettings;
	}, [account?.features?.boards?.matterBoard?.configuration?.tagSettings]);

	const onSurveysDefaultReviewInputBlur = React.useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			let reviewUrl = e.target.value?.trim();
			if (reviewUrl && !/^http[s]?:\/\//gim.test(reviewUrl)) {
				reviewUrl = `https://${reviewUrl}`;
			}
			onSetSurveyDefaultReviewLink?.(reviewUrl);
		},
		[onSetSurveyDefaultReviewLink]
	);
	const onSurveysDefaultReviewInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPendingSurveysDefaultReviewUrl(e.target.value);
	}, []);

	const onWebsitesWebsiteUrlInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setWebsitesWebsiteUrl(e.target.value);
	}, []);

	const onWebsitesDudaSiteIdInputChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setWebsitesDudaSiteId(e.target.value);
	}, []);

	const onWebsitesDudaSiteIdBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = e.target.value?.trim();
		onWebsitesDudaSiteIdChanged?.(value);
	};

	const onWebsitesWebsitesUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = e.target.value?.trim();
		onWebsitesWebsiteUrlChanged?.(value);
	};

	const onManageCustomBrandingClick = React.useCallback(() => {
		const { origin, pathname } = window.location;
		const accountId = account?.id;
		if (accountId) {
			openUrlInNewTab(`${origin}${pathname}#/accounts/${accountId}/template-designer`);
		}
	}, [account?.id]);

	const textingDailySendLimit = account?.features?.texting?.sendLimits?.find(x => x.intervalInMinutes === 1440)?.count;
	const [pendingTextingDailySendLimit, setPendingTextingDailySendLimit] = useState<string>(
		textingDailySendLimit?.toString() || ''
	);
	const turningXXAge = account?.preferences?.resourceSelectorSettings?.TurningXX?.age;
	const [turningXXYearsDashboardCard, setTurningXXYearsDashboardCard] = useState<string>(
		turningXXAge?.toString() || ''
	);
	const turningXX2Age = account?.preferences?.resourceSelectorSettings?.TurningXX2?.age;
	const [turningXX2YearsDashboardCard, setTurningXX2YearsDashboardCard] = useState<string>(
		turningXX2Age?.toString() || ''
	);
	const turningXX3Age = account?.preferences?.resourceSelectorSettings?.TurningXX3?.age;
	const [turningXX3YearsDashboardCard, setTurningXX3YearsDashboardCard] = useState<string>(
		turningXX3Age?.toString() || ''
	);
	const turningXX4Age = account?.preferences?.resourceSelectorSettings?.TurningXX4?.age;
	const [turningXX4YearsDashboardCard, setTurningXX4YearsDashboardCard] = useState<string>(
		turningXX4Age?.toString() || ''
	);
	const turningXX5Age = account?.preferences?.resourceSelectorSettings?.TurningXX5?.age;
	const [turningXX5YearsDashboardCard, setTurningXX5YearsDashboardCard] = useState<string>(
		turningXX5Age?.toString() || ''
	);
	const onTextingDailySendLimitInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value.trim());
		if (e.target.value.length > 0 && value !== textingDailySendLimit) {
			onTextingDailySendLimitChanged?.(value);
		}
	};

	const textingHourlySendLimit = account?.features?.texting?.sendLimits?.find(x => x.intervalInMinutes === 60)?.count;
	const [pendingTextingHourlySendLimit, setPendingTextingHourlySendLimit] = useState<string>(
		textingHourlySendLimit?.toString() || ''
	);
	const onTextingHourlySendLimitInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value !== textingHourlySendLimit) {
			onTextingHourlySendLimitChanged?.(value);
		}
	};

	const textingSecondsBetweenSends = account?.features?.texting?.secondsBetweenSends;
	const [pendingTextingSecondsBetweenSends, setPendingTextingSecondsBetweenSends] = useState<string>(
		textingSecondsBetweenSends?.toString() || ''
	);

	// Default Email Style feature state management
	const defaultEmailStyle = account?.features?.defaultEmailStyle;
	const [pendingDefaultEmailStyleEnabled, setPendingDefaultEmailStyleEnabled] = useState<boolean>(
		defaultEmailStyle?.enabled || false
	);
	const [pendingDefaultEmailFontFace, setPendingDefaultEmailFontFace] = useState<string>(
		defaultEmailStyle?.fontFace || 'arial,helvetica,sans-serif'
	);
	const [pendingDefaultEmailFontSize, setPendingDefaultEmailFontSize] = useState<string>(
		defaultEmailStyle?.fontSize || '14px'
	);

	// Sync local state with account data when it changes
	React.useEffect(() => {
		if (defaultEmailStyle) {
			setPendingDefaultEmailStyleEnabled(defaultEmailStyle.enabled || false);
			setPendingDefaultEmailFontFace(defaultEmailStyle.fontFace || 'arial,helvetica,sans-serif');
			setPendingDefaultEmailFontSize(defaultEmailStyle.fontSize || '14px');
		}
	}, [defaultEmailStyle]);

	// Default Email Style change handlers
	const handleDefaultEmailStyleEnabledChanged = React.useCallback(
		(enabled: boolean) => {
			setPendingDefaultEmailStyleEnabled(enabled);
			onDefaultEmailStyleEnabledChanged?.(enabled);
		},
		[onDefaultEmailStyleEnabledChanged]
	);

	const handleDefaultEmailFontFaceChanged = React.useCallback(
		(option: ISelectOption<string>) => {
			if (option?.dataContext) {
				setPendingDefaultEmailFontFace(option.dataContext);
				onDefaultEmailStyleFontFaceChanged?.(option.dataContext);
			}
		},
		[onDefaultEmailStyleFontFaceChanged]
	);

	const handleDefaultEmailFontSizeChanged = React.useCallback(
		(option: ISelectOption<string>) => {
			if (option?.dataContext) {
				setPendingDefaultEmailFontSize(option.dataContext);
				onDefaultEmailStyleFontSizeChanged?.(option.dataContext);
			}
		},
		[onDefaultEmailStyleFontSizeChanged]
	);

	const onTextingSecondsBetweenSendsInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== textingSecondsBetweenSends) {
			onTextingSecondsBetweenSendsChanged?.(value);
		}
	};

	const onTurningXXInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== turningXXAge) {
			onTurningXXInputChanged?.(value);
		}
	};

	const onTurningXX2InputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== turningXX2Age) {
			onTurningXX2InputChanged?.(value);
		}
	};

	const onTurningXX3InputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== turningXX3Age) {
			onTurningXX3InputChanged?.(value);
		}
	};

	const onTurningXX4InputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== turningXX4Age) {
			onTurningXX4InputChanged?.(value);
		}
	};

	const onTurningXX5InputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = Number(e.target.value?.trim());
		if (value > 0 && value !== turningXX5Age) {
			onTurningXX5InputChanged?.(value);
		}
	};

	const onTextingInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const targetVal = e.target.value;
		const name = e.target.name;
		const value = Number(targetVal);
		if (!isNaN(value) && value >= 0 && value < 5000) {
			if (name === SettingName.TextingDailySendLimit) {
				setPendingTextingDailySendLimit(targetVal);
			} else if (name === SettingName.TextingHourlySendLimit) {
				setPendingTextingHourlySendLimit(targetVal);
			} else if (name === SettingName.ShowTurningXXDashboardCard && value <= 100) {
				setTurningXXYearsDashboardCard(targetVal);
			} else if (name === SettingName.ShowTurningXX2DashboardCard && value <= 100) {
				setTurningXX2YearsDashboardCard(targetVal);
			} else if (name === SettingName.ShowTurningXX3DashboardCard && value <= 100) {
				setTurningXX3YearsDashboardCard(targetVal);
			} else if (name === SettingName.ShowTurningXX4DashboardCard && value <= 100) {
				setTurningXX4YearsDashboardCard(targetVal);
			} else if (name === SettingName.ShowTurningXX5DashboardCard && value <= 100) {
				setTurningXX5YearsDashboardCard(targetVal);
			} else {
				if (value > 0) {
					setPendingTextingSecondsBetweenSends(e.target.value);
				}
			}
		}
	};

	const handleSubmittedCustomCard = (submittedValues: ISubmittedValues, card?: TCustomSettings) => {
		if (card) {
			onUpdateCustomCard(submittedValues, card);
			return;
		}
		onAddCustomCard(submittedValues);
	};

	const onEditCustomCard = (card: TCustomSettings) => {
		setEditCustomCard(card);
		onShowModal();
	};

	const onDisableCard = (card: TCustomSettings) => {
		setEditCustomCard(card);
		setConfirmDisable(true);
	};

	const onCustomFieldSave = () => {
		onAddCustomField();
		setShowCreateCustomFieldsModal(false);
		customFieldsQuery.refetch();
	};

	const onCustomFieldCancel = () => {
		setShowCreateCustomFieldsModal(false);
	};

	const getCustomCard = (cardType: TCustomSettings) => {
		const CARD_TYPE = account?.preferences?.resourceSelectorSettings?.[cardType];

		return (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: `Disabling the ${CARD_TYPE.displayName} card will delete it from the account`,
					label: (
						<CustomLabel
							button={
								<button
									type='button'
									className={css(styleSheet.customEditButton)}
									onClick={() => onEditCustomCard(cardType)}
								>
									<EditItemPenIcon fillColor={actionItemTintColor} />
								</button>
							}
						>
							{`${CARD_TYPE.displayName} Dashboard Card`}
						</CustomLabel>
					),
					onSelectionChange: (val: ISelectOption<number>) => {
						onUpdateCustomCardDays(val, cardType);
					},
					onToggle: () => onDisableCard(cardType),
					options: CustomCardDaysOptions,
					selectedOption: getCustomCardsOption(cardType),
					type: SettingType.Multi,
					value: !(CARD_TYPE?.daysToLookAhead === 0 || CARD_TYPE?.daysToLookAhead === undefined),
				}}
				key={`${cardType}`}
			/>
		);
	};

	let settings = [
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.TwoFactorAuthentication,
				onToggle: onTwoFactorAuthChanged,
				type: SettingType.Toggle,
				value: account?.requireSecondAuthFactor,
			}}
			key={SettingName.TwoFactorAuthentication}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.AllowOAuthLogin,
				onToggle: onAllowOAuthLoginChanged,
				type: SettingType.Toggle,
				value: account?.oAuthLoginSettings?.allowOAuthLogin,
			}}
			key={SettingName.AllowOAuthLogin}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			disabled={!account?.oAuthLoginSettings?.allowOAuthLogin}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.OnlyAllowOAuthLogin,
				onToggle: onOnlyAllowOAuthLoginChanged,
				type: SettingType.Toggle,
				value: account?.oAuthLoginSettings?.onlyAllowOAuthLogin,
			}}
			key={SettingName.OnlyAllowOAuthLogin}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			disabled={!account?.oAuthLoginSettings?.allowOAuthLogin}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.EnabledOAuthProviders,
				type: SettingType.MultiSelect,
				onMultiSelectionChange: handleOAuthProvidersChange,
				options: oAuthProviderOptions,
				selectedOptions: selectedOAuthProviders,
			}}
			key={SettingName.EnabledOAuthProviders}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Note: This feature is only available from some providers',
				label: SettingName.CreateContactsFromCalendar,
				onToggle: onCreateContactsFromCalendarChanged,
				type: SettingType.Toggle,
				value: !account?.preferences?.disableCalendarContactSync,
			}}
			key={SettingName.CreateContactsFromCalendar}
		/>,
		<Setting<UserRole>
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.DefaultUserRole,
				onSelectionChange: onDefaultUserRoleChanged,
				options: UserOptions,
				selectedOption:
					UserOptions.find(o => o.dataContext === account?.preferences?.defaults?.userRole) ??
					UserOptions.find(o => o.dataContext === UserRole.User),
				type: SettingType.Dropdown,
			}}
			key={SettingName.DefaultUserRole}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Default Privacy when a new contact is added to the account by any user',
				label: SettingName.DefaultContactVisibility,
				onSelectionChange: onDefaultContactsVisibilityChanged,
				options: PrivacyOptions,
				selectedOption:
					PrivacyOptions.find(o => o.dataContext === account?.preferences?.defaults?.visibility) ??
					PrivacyOptions.find(o => o.dataContext === Privacy.All),
				type: SettingType.Dropdown,
			}}
			key={SettingName.DefaultContactVisibility}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Located under the Advanced Options tab in the email composer',
				label: SettingName.CcEmployeeDuringSend,
				onToggle: onEnableCcInCampaignsChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.enableCcInCampaigns,
			}}
			key={SettingName.CcEmployeeDuringSend}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.AllowCsmToViewCampaign,
				onToggle: onCsmViewMyCampaignsEnabledChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.csmViewMyCampaignsEnabled,
			}}
			key={SettingName.AllowCsmToViewCampaign}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'More commonly referred to as "Send on behalf". Also applies to admins approving automations.',
				label: SettingName.SendCampaignsOnBehalf,
				onToggle: onSendOnBehalfChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.sendOnBehalfEnabled,
			}}
			key={SettingName.SendCampaignsOnBehalf}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info:
					account?.additionalInfo?.industry === Industry.Insurance
						? 'Includes ability to send turning 65 email.'
						: null,
				label:
					account?.additionalInfo?.industry === Industry.Insurance
						? SettingName.SendBdayRenewalEmail
						: account?.additionalInfo?.industry === Industry.Financial
							? SettingName.SendBdayFinancialReviewEmail
							: SettingName.SendBdayEmail,
				onSelectionChange: onAdminSendOnBehalfContactOwnerCriteriaChanged,
				options: ContactOwnerCriteriaOptions,
				selectedOption: getContactOwnerForResourceSelectorOptions(ResourceSelectorId.HappyBirthday),
				type: SettingType.Dropdown,
			}}
			disabled={!account?.preferences?.sendOnBehalfEnabled}
			key={SettingName.SendBdayRenewalEmail}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Minimum wait time before Send on Behalf emails can be sent - allowing for notification to the sender.',
				label: SettingName.WaitTimeForSendOnBehalf,
				onSelectionChange: onSendOnBehalfWaitTimeChanged,
				options: SendOnBehalfWaitTimeOptions,
				selectedOption: getSendOnBehalfWaitTimeOption(),
				type: SettingType.Dropdown,
			}}
			disabled={!account?.preferences?.sendOnBehalfEnabled}
			key={SettingName.WaitTimeForSendOnBehalf}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.ConnectionTypes,
				onToggle: onConnectionTypesEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.connectionTypes?.enabled,
			}}
			disabled={!account?.preferences?.sendOnBehalfEnabled}
			key={SettingName.ConnectionTypes}
			styles={[styleSheet.subRow]}
		/>,
		...(account?.features?.websites?.enabled
			? [
					<Setting
						canModifyAccount={canModifyAccount}
						data={{
							category: SettingCategory.Features,
							label: SettingName.Websites,
							info: 'Websites are enabled for this account. This is determined by plan type.',
							onToggle: onWebsitesChanged,
							type: SettingType.Toggle,
							value: account?.features?.websites?.enabled,
							settingDisabled: true,
						}}
						key={SettingName.Websites}
					/>,
					<Setting
						canModifyAccount={canModifyAccount}
						data={{
							category: SettingCategory.Features,
							label: SettingName.WebsitesDudaSiteId,
							type: SettingType.Input,
							inputProps: {
								onBlur: onWebsitesDudaSiteIdBlur,
								onChange: onWebsitesDudaSiteIdInputChanged,
								value: websitesDudaSiteId,
							},
						}}
						key={SettingName.WebsitesDudaSiteId}
						styles={[styleSheet.subRow]}
					/>,
					<Setting
						canModifyAccount={canModifyAccount}
						data={{
							category: SettingCategory.Features,
							label: SettingName.WebsitesWebsiteUrl,
							type: SettingType.Input,
							inputProps: {
								onBlur: onWebsitesWebsitesUrlBlur,
								onChange: onWebsitesWebsiteUrlInputChanged,
								value: websitesWebsiteUrl,
							},
						}}
						key={SettingName.WebsitesWebsiteUrl}
						styles={[styleSheet.subRow]}
					/>,
				]
			: []),
		<Setting<EnhancementType>
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				info: 'Contact enrichment from FullContact. It is a best effort service to round out contact/company profiles. Disabling this setting will not remove already loaded information.',
				label: SettingName.ProfileEnrichment,
				onSelectionChange: onSetEnhancementOption,
				options: EnhancementOptions,
				selectedOption: getEnhancementSetting(),
				type: SettingType.Dropdown,
			}}
			key={SettingName.ProfileEnrichment}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.WeeklyTaggingGameReminder,
				onToggle: onClassificationEmailsChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.classificationEmailsEnabled,
			}}
			key={SettingName.WeeklyTaggingGameReminder}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				label: SettingName.ShowUpcomingBirthdaysTo,
				onSelectionChange: onSetShowUpcomingBirthdaysTo,
				options: UpcomingKeyDateDisplayScopes,
				selectedOption: getDisplayScope(),
				type: SettingType.Dropdown,
			}}
			key={SettingName.ShowUpcomingBirthdaysTo}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				label: SettingName.ShowInternalMeetings,
				onToggle: onInternalMeetingsChanged,
				type: SettingType.Toggle,
				value: !account?.preferences?.ignoreInternalMeetings,
			}}
			key={SettingName.ShowInternalMeetings}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				label: SettingName.TrackOpenedEmails,
				onToggle: onSentMailOpenTrackingChanged,
				type: SettingType.Toggle,
				value: !account?.preferences?.disableSentMailOpenTracking,
			}}
			key={SettingName.TrackOpenedEmails}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				label: SettingName.TrackClickedEmails,
				onToggle: onSentMailClickTrackingChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.clickTrackingEnabled,
			}}
			key={SettingName.TrackClickedEmails}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				info: 'A global enablement for lead parsers. Enabling will turn them all on.',
				label: SettingName.ScanEmailForLeads,
				onToggle: onLookForLeadsChanged,
				type: SettingType.Toggle,
				value: account?.features?.emailScan?.enabledScanners?.length > 0,
			}}
			key={SettingName.ScanEmailForLeads}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Enable this for customers to data refresh from Importing Contacts',
				label: SettingName.DeleteRenewalKeyFacts,
				onToggle: onDeleteRenewalKeyFactsChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.deleteRenewalKeyFacts,
			}}
			key={SettingName.DeleteRenewalKeyFacts}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				label: SettingName.LogScannedEmails,
				info: 'Logs all emails scanned for leads in the account. When enabled, it will only be enabled for 14 days.',
				onToggle: onSaveForAnalysisChanged,
				type: SettingType.Toggle,
				value: account?.features?.emailScan?.saveForAnalysis,
			}}
			key={SettingName.LogScannedEmails}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.HIPAA,
				info: account?.features?.hipaa?.enabled
					? 'HIPAA is enabled. To disable HIPAA, please submit a support ticket.'
					: 'Enable HIPAA compliance features for this account. Once enabled, disabling requires a support ticket. This needs to be performed in coordination with the customer.',
				onToggle: account?.features?.hipaa?.enabled
					? undefined
					: (checked: boolean) => checked && onHIPAAEnabledChanged?.(true),
				type: SettingType.Toggle,
				value: account?.features?.hipaa?.enabled,
				settingDisabled: account?.features?.hipaa?.enabled,
			}}
			key={SettingName.HIPAA}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.HTMLNewsletters,
				onToggle: onHtmlNewsletterChanged,
				type: SettingType.Toggle,
				value: account?.features.htmlNewsletter?.enabled,
			}}
			key={SettingName.HTMLNewsletters}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: (
					<button type='button' className={css(styleSheet.customTextColor)} onClick={onEditHtmlNewsletterFooter}>
						{SettingName.EditNewsletterSettings}
					</button>
				),
				onToggle: onHtmlNewsletterChanged,
				type: SettingType.Custom,
				value: account?.features.htmlNewsletter?.enabled,
			}}
			disabled={!account?.features.htmlNewsletter.enabled}
			key={SettingName.EditNewsletterSettings}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				info: 'Set the maximum number of contacts that can be contacted via HTML Newsletter.',
				label: SettingName.MaximumHTMLNewsletterContacts,
				onSelectionChange: onSetMaximumHTMLNewsletterContacts,
				options: MaximumHTMLNewsletterContacts,
				selectedOption: getMaximumHTMLNewsletterContacts(),
				type: SettingType.Dropdown,
			}}
			key={SettingName.MaximumHTMLNewsletterContacts}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Automations,
				onToggle: toggleAutomations,
				type: SettingType.Toggle,
				value: account?.features?.automation?.enabled,
			}}
			key={SettingName.Automations}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.TextingCampaignAutomationsTrigger,
				onToggle: setTextingCampaignAutomationTriggerEnabled,
				type: SettingType.Toggle,
				value: () => {
					const triggers = new Set(account?.features?.automation.additionalAllowedTriggers || []);
					return triggers.has(AutomationTriggerType.Texting);
				},
			}}
			disabled={!account?.features?.automation?.enabled || !account?.features?.texting?.enabled}
			key={SettingName.TextingCampaignAutomationsTrigger}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.AllowTextingInTagAutomations,
				onToggle: setTagAutomationTriggerEnabled,
				type: SettingType.Toggle,
				value: () => {
					const triggers = new Set(account?.features?.automation.additionalAllowedTextStepTriggers || []);
					return triggers.has(AutomationTriggerType.Tag);
				},
			}}
			disabled={!account?.features?.automation?.enabled}
			key={SettingName.AllowTextingInTagAutomations}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.AllowTextingInManualAutomations,
				onToggle: setManualAutomationTriggerEnabled,
				type: SettingType.Toggle,
				value: () => {
					const triggers = new Set(account?.features?.automation.additionalAllowedTextStepTriggers || []);
					return triggers.has(AutomationTriggerType.Manual);
				},
			}}
			disabled={!account?.features?.automation?.enabled}
			key={SettingName.AllowTextingInManualAutomations}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.AllowToStartOnBehalf,
				onToggle: setStartOnBehalfTriggerEnabled,
				type: SettingType.Toggle,
				value: account?.features?.automation.allowAdminToStartOnBehalf,
			}}
			disabled={!account?.features?.automation?.enabled}
			key={SettingName.AllowToStartOnBehalf}
			styles={[styleSheet.subRow]}
		/>,
		isDevOrTestEnvironment ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: SettingName.AllowStartAutomationsOnHold,
					onToggle: onSetAllowStartAutomationsOnHold,
					type: SettingType.Toggle,
					value: account?.features?.automation?.allowStartAutomationsOnHold,
				}}
				disabled={!account?.features?.automation?.enabled}
				key={SettingName.AllowStartAutomationsOnHold}
				styles={[styleSheet.subRow]}
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.ShowInternalSendOption,
				onToggle: onSetShowInternalSendOption,
				type: SettingType.Toggle,
				value: account?.features?.automation.showInternalSendOption,
			}}
			disabled={!account?.features?.automation?.enabled}
			key={SettingName.ShowInternalSendOption}
			styles={[styleSheet.subRow]}
		/>,
		<Setting<UserRole>
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				info: 'Users without this role will only be able to see their own opportunities.',
				label: SettingName.RequiredRoleToViewAllOpportunities,
				onSelectionChange: onRequiredRoleToViewAllOpportunities,
				options: RoleForOpportunitiesOptions,
				selectedOption:
					RoleForOpportunitiesOptions.find(
						o => o.dataContext === account?.preferences?.requiredRoleToViewAllOpportunities
					) ?? RoleForOpportunitiesOptions.find(o => o.dataContext === UserRole.User),
				type: SettingType.Dropdown,
			}}
			key={SettingName.RequiredRoleToViewAllOpportunities}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.DataBoardDonations,
				onToggle: onDataBoardDonationsEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.boards?.donationBoard?.enabled,
			}}
			key={SettingName.DataBoardDonations}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			customText='Configure'
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.DonationTagging,
				type: SettingType.Custom,
			}}
			disabled={!account?.features?.boards?.donationBoard?.enabled}
			key={SettingName.DonationTagging}
			onCustomCallback={onOpenDonationTagSettings}
			styles={[styleSheet.subRow]}
		/>,
		onClearAllDonations ? (
			<Setting
				canModifyAccount={hasSupportPermission}
				data={{
					category: SettingCategory.Databoards,
					info: 'Archives all donations for this account.',
					label: (
						<button
							className={css(styleSheet.exportButton)}
							onClick={onClearAllDonations}
							disabled={!hasSupportPermission}
						>
							<TrashIcon className={css(styleSheet.columnButtonIcon)} />
							<span style={{ marginLeft: 10 }}>{SettingName.ArchiveAllDonations}</span>
						</button>
					),
					type: SettingType.Custom,
				}}
				disabled={!account?.features?.boards?.donationBoard?.enabled}
				key={SettingName.ArchiveAllDonations}
				styles={[styleSheet.subRow]}
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.DataBoardPolicies,
				onToggle: onDataBoardPoliciesEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.boards?.policyBoard?.enabled ?? false,
			}}
			key={`${SettingName.DataBoardPolicies}-${SettingName.DataBoardPolicies}`}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.CarrierTags,
				onToggle: onDataBoardPoliciesCarrierTagsEnabledChanged,
				type: SettingType.Toggle,
				value:
					(account?.features?.boards?.policyBoard?.configuration?.tagSettings as IPolicyDataboardTagSettings)
						?.carrierTagsEnabled ?? true,
			}}
			disabled={!account?.features?.boards?.policyBoard?.enabled || !account?.features?.boards?.policiesEnabled}
			key={SettingName.CarrierTags}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.LineOfBusinessTags,
				onToggle: onDataBoardPoliciesLineOfBusinessTagsEnabledChanged,
				type: SettingType.Toggle,
				value:
					(account?.features?.boards?.policyBoard?.configuration?.tagSettings as IPolicyDataboardTagSettings)
						?.lobTagEnabled ?? true,
			}}
			disabled={!account?.features?.boards?.policyBoard?.enabled || !account?.features?.boards?.policiesEnabled}
			key={SettingName.LineOfBusinessTags}
			styles={[styleSheet.subRow]}
		/>,
		...(account?.additionalInfo?.industry === Industry.Legal
			? [
					<Setting
						canModifyAccount={canModifyAccount}
						data={{
							category: SettingCategory.Databoards,
							label: SettingName.DataBoardMatters,
							onToggle: onDataBoardMattersEnabledChanged,
							type: SettingType.Toggle,
							value: getMatterBoardEnabled(),
						}}
						key={SettingName.DataBoardMatters}
					/>,
					<Setting
						canModifyAccount={canModifyAccount}
						data={{
							category: SettingCategory.Databoards,
							label: SettingName.MatterStageTags,
							onToggle: onDataBoardMattersMatterStageTagsEnabledChanged,
							type: SettingType.Toggle,
							value: getMatterBoardTagSettings()?.matterStageTagEnabled ?? true,
						}}
						disabled={!getMatterBoardEnabled() || !getMatterBoardTagSettings()?.enabled}
						key={SettingName.MatterStageTags}
						styles={[styleSheet.subRow]}
					/>,
				]
			: []),
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Databoards,
				label: SettingName.DataBoardOpportunities,
				onToggle: onDataBoardOpportunitiesEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.boards?.opportunitiesEnabled,
			}}
			key={SettingName.DataBoardOpportunities}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.ScheduleAMeeting,
				onToggle: onMeetingSchedulerEnabled,
				type: SettingType.Toggle,
				value: account?.features?.meetingScheduler?.enabled,
			}}
			key={SettingName.ScheduleAMeeting}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.AllowCompanyNameRequired,
				onToggle: onMeetingCompanyNameRequiredChanged,
				type: SettingType.Toggle,
				value: account?.features?.meetingScheduler?.allowCompanyNameRequired,
			}}
			disabled={!account?.features?.meetingScheduler?.enabled}
			key={SettingName.AllowCompanyNameRequired}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				info: 'Notifies user of new contacts created by lead parsers',
				label: SettingName.SendEmailForNewLeads,
				onToggle: onSendEmailForNewLeadChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.sendNewLeadEmail,
			}}
			key={SettingName.SendEmailForNewLeads}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.EmailTracking,
				info: 'Initial setting for save email as note.',
				label: SettingName.SaveEmailAsNoteDefault,
				onToggle: checked => onToggleAccountPreferenceBoolean('saveEmailAsNoteDefault', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.saveEmailAsNoteDefault,
			}}
			key={SettingName.SaveEmailAsNoteDefault}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Any user on the account will be able to edit notes made by other users.',
				label: SettingName.AnyUserCanEditNotes,
				onToggle: onSetNoteViewerCanEditEnabledChanged,
				type: SettingType.Toggle,
				value: account?.preferences.noteViewerCanEditEnabled,
			}}
			key={SettingName.AnyUserCanEditNotes}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Set the maximum number of contacts that can be contacted via group automation.',
				label: SettingName.MaximumGroupAutomationContacts,
				onSelectionChange: onSetMaximumGroupAutomationContacts,
				options: MaximumGroupAutomationContacts,
				selectedOption: getMaximumGroupAutomationContacts(),
				type: SettingType.Dropdown,
			}}
			key={SettingName.MaximumGroupAutomationContacts}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Set the maximum number of contacts that can be contacted via group send.',
				label: SettingName.MaximumGroupSendContacts,
				onSelectionChange: onSetMaximumGroupSendContacts,
				options: MaximumGroupSendContacts,
				selectedOption: getMaximumGroupSendContacts(),
				type: SettingType.Dropdown,
			}}
			key={SettingName.MaximumGroupSendContacts}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Maximum number of emails an account can send in a 24-hour period. A support ticket is required to change.',
				label: SettingName.DailyEmailMaximum,
				inputProps: {
					styles: [styleSheet.numericInput],
					name: SettingName.DailyEmailMaximum,
					placeholder: account?.features.sendEmail.maxEmailsPerDay?.toString(),
					value: account?.features.sendEmail.maxEmailsPerDay,
				},
				settingDisabled: true,
				type: SettingType.Input,
			}}
			key={SettingName.DailyEmailMaximum}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will suppress any contacts removed from key date sends. This means they will not appear in the future. It is on by default',
				label: SettingName.SuppressContactsRemovedFromKeyDateSends,
				onToggle: onSetSuppressContactsRemovedFromResourceSends,
				type: SettingType.Toggle,
				value: account?.preferences.suppressContactsRemovedFromResourceSends,
			}}
			key={SettingName.SuppressContactsRemovedFromKeyDateSends}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.ImportFromProviderEnabled,
				onToggle: checked => onToggleAccountPreferenceBoolean('importFromProviderEnabled', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.importFromProviderEnabled,
			}}
			key={SettingName.ImportFromProviderEnabled}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.CreateContactsFromSentMessages,
				onToggle: checked => onToggleAccountPreferenceBoolean('createContactsFromSentMessages', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.createContactsFromSentMessages,
			}}
			key={SettingName.CreateContactsFromSentMessages}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.StoreSubjectInCommunicationHistory,
				info: 'Create Contacts From Sent Emails should be enabled as well. Turning on automatically triggers reloading history.',
				onToggle: checked => onToggleAccountPreferenceBoolean('storeSubjectInCommunicationHistory', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.storeSubjectInCommunicationHistory,
			}}
			key={SettingName.StoreSubjectInCommunicationHistory}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.DigestEmails,
				onToggle: checked => onToggleAccountPreferenceBoolean('digestEmailsEnabled', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.digestEmailsEnabled,
			}}
			key={SettingName.DigestEmails}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their 65th birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				label: SettingName.ShowTurning65DashboardCards,
				onSelectionChange: onSetTurning65MonthsOption,
				onToggle: onSetTurning65MonthsOptionChange,
				options: allTurning65Options,
				selectedOption: getTurning65CardsOption(),
				type: SettingType.Multi,
				value: !(
					account?.preferences?.resourceSelectorSettings?.Turning65?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.Turning65?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurning65DashboardCards}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their XX birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				inputProps: {
					name: SettingName.ShowTurningXXDashboardCard,
					onBlur: onTurningXXInputBlur,
					onChange: onTextingInputChanged,
					placeholder: turningXXAge?.toString(),
					value: turningXXYearsDashboardCard,
				},
				label: SettingName.ShowTurningXXDashboardCard,
				onSelectionChange: onSetTurningXXMonthsOption,
				onToggle: onSetTurningXXMonthsOptionChange,
				options: TurningXXOptions,
				selectedOption: getTurningXXCardsOption(),
				type: SettingType.MultiWInput,
				value: !(
					account?.preferences?.resourceSelectorSettings?.TurningXX?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.TurningXX?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurningXXDashboardCard}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their XX birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				inputProps: {
					name: SettingName.ShowTurningXX2DashboardCard,
					onBlur: onTurningXX2InputBlur,
					onChange: onTextingInputChanged,
					placeholder: turningXX2Age?.toString(),
					value: turningXX2YearsDashboardCard,
				},
				label: SettingName.ShowTurningXX2DashboardCard,
				onSelectionChange: onSetTurningXX2MonthsOption,
				onToggle: onSetTurningXX2MonthsOptionChange,
				options: TurningXXOptions,
				selectedOption: getTurningXX2CardsOption(),
				type: SettingType.MultiWInput,
				value: !(
					account?.preferences?.resourceSelectorSettings?.TurningXX2?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.TurningXX2?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurningXX2DashboardCard}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their XX birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				inputProps: {
					name: SettingName.ShowTurningXX3DashboardCard,
					onBlur: onTurningXX3InputBlur,
					onChange: onTextingInputChanged,
					placeholder: turningXX3Age?.toString(),
					value: turningXX3YearsDashboardCard,
				},
				label: SettingName.ShowTurningXX3DashboardCard,
				onSelectionChange: onSetTurningXX3MonthsOption,
				onToggle: onSetTurningXX3MonthsOptionChange,
				options: TurningXX3Options,
				selectedOption: getTurningXX3CardsOption(),
				type: SettingType.MultiWInput,
				value: !(
					account?.preferences?.resourceSelectorSettings?.TurningXX3?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.TurningXX3?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurningXX3DashboardCard}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their XX birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				inputProps: {
					name: SettingName.ShowTurningXX4DashboardCard,
					onBlur: onTurningXX4InputBlur,
					onChange: onTextingInputChanged,
					placeholder: turningXX4Age?.toString(),
					value: turningXX4YearsDashboardCard,
				},
				label: SettingName.ShowTurningXX4DashboardCard,
				onSelectionChange: onSetTurningXX4MonthsOption,
				onToggle: onSetTurningXX4MonthsOptionChange,
				options: TurningXX4Options,
				selectedOption: getTurningXX4CardsOption(),
				type: SettingType.MultiWInput,
				value: !(
					account?.preferences?.resourceSelectorSettings?.TurningXX4?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.TurningXX4?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurningXX4DashboardCard}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show any customers who have their XX birthday in the next 60, 90, 120 or 365 days (from the end of the current month). Disabled by default.',
				inputProps: {
					name: SettingName.ShowTurningXX5DashboardCard,
					onBlur: onTurningXX5InputBlur,
					onChange: onTextingInputChanged,
					placeholder: turningXX5Age?.toString(),
					value: turningXX5YearsDashboardCard,
				},
				label: SettingName.ShowTurningXX5DashboardCard,
				onSelectionChange: onSetTurningXX5MonthsOption,
				onToggle: onSetTurningXX5MonthsOptionChange,
				options: TurningXX5Options,
				selectedOption: getTurningXX5CardsOption(),
				type: SettingType.MultiWInput,
				value: !(
					account?.preferences?.resourceSelectorSettings?.TurningXX5?.daysToLookAhead === 0 ||
					account?.preferences?.resourceSelectorSettings?.TurningXX5?.daysToLookAhead === undefined
				),
			}}
			key={SettingName.ShowTurningXX5DashboardCard}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'In some cases, Smart Salutations can filter out correct names. If this occurs, disable the setting to only return the first names in salutations.',
				label: SettingName.SmartSalutationsEnabled,
				onToggle: checked => onToggleAccountPreferenceBoolean('smartSalutationsEnabled', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.smartSalutationsEnabled,
			}}
			key={SettingName.SmartSalutationsEnabled}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Permanent,
				info: 'Warning! You are about to reset this entire account. This will permanently clear the account of contacts, companies and tags',
				label: (
					<button
						onClick={!canModifyAccount ? undefined : onResetAccountClicked}
						className={css(styleSheet.resetAccount)}
					>
						{SettingName.ResetAccount}
					</button>
				),
				type: SettingType.Custom,
			}}
			key={SettingName.ResetAccount}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Permanent,
				info: 'We will send you an email with the data in csv format',
				label: (
					<ExportDataButton
						text={SettingName.ExportNotes}
						exportDataType={ExportDataType.Notes}
						impersonationContext={{ account }}
						className={css(styleSheet.exportButton)}
						disabled={!hasDevPermission && !hasSupportPermission}
					/>
				),
				type: SettingType.Custom,
			}}
			key={SettingName.ExportNotes}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Permanent,
				info: 'We will send you an email with the data in csv format',
				label: (
					<ExportDataButton
						text={SettingName.ExportTexts}
						exportDataType={ExportDataType.Texts}
						impersonationContext={{ account }}
						className={css(styleSheet.exportButton)}
						disabled={!hasDevPermission && !hasSupportPermission}
					/>
				),
				type: SettingType.Custom,
			}}
			key={SettingName.ExportTexts}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Permanent,
				info: 'We will send you an email with the data in csv format',
				label: (
					<ExportDataButton
						text={SettingName.ExportPosts}
						exportDataType={ExportDataType.Posts}
						impersonationContext={{ account }}
						className={css(styleSheet.exportButton)}
						disabled={!hasDevPermission && !hasSupportPermission}
					/>
				),
				type: SettingType.Custom,
			}}
			key={SettingName.ExportPosts}
		/>,

		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: "If enabled, any email send will always BCC the sender's configured BCC (if any).",
				label: SettingName.UseUserBccEmailAlwaysEnabled,
				onToggle: checked => onToggleAccountPreferenceBoolean('useUserBccAlwaysEnabled', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.useUserBccAlwaysEnabled,
			}}
			key={SettingName.UseUserBccEmailAlwaysEnabled}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.SocialMedia,
				onToggle: onEnableSocialMediaChanged,
				type: SettingType.Toggle,
				value: account?.features?.socialMedia?.enabled,
			}}
			key={SettingName.SocialMedia}
		/>,
		account?.features?.socialMedia.enabled ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: SettingName.SocialMediaLogoOverlay,
					onToggle: onEnableSocialMediaLogoOverlayChanged,
					type: SettingType.Toggle,
					value: account?.features?.socialMedia?.alwaysOverlayLogo,
				}}
				styles={[styleSheet.subRow]}
				key={SettingName.SocialMediaLogoOverlay}
			/>
		) : null,
		account?.features?.socialMedia.enabled ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: SettingName.SocialMediaHideBackground,
					onToggle: onEnableSocialMediaLogoBackgroundChanged,
					type: SettingType.Toggle,
					value: account?.features?.socialMedia?.hideWhiteBackground,
				}}
				styles={[styleSheet.subRow]}
				key={SettingName.SocialMediaHideBackground}
			/>
		) : null,
		account?.features?.socialMedia.enabled && (isDevOrSupport || account?.features?.socialMedia.readOnly) ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: SettingName.SocialReadOnly,
					onToggle: onEnableSocialMediaReadOnly,
					type: SettingType.Toggle,
					info: 'Allows users to see social media content but not connect. A maintenance ticket is required to turn this off.',
					value: account?.features?.socialMedia?.readOnly,
				}}
				styles={[styleSheet.subRow]}
				key={SettingName.SocialReadOnly}
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.BlogEnabled,
				onToggle: () => onBlogEnabledChanged?.(!account?.features?.blogFeature?.enabled),
				type: SettingType.Toggle,
				value: account?.features?.blogFeature?.enabled,
			}}
			key={SettingName.BlogEnabled}
		/>,
		account?.features?.blogFeature?.enabled ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: (
						<a
							className={css(baseStyleSheet.brandLink)}
							href={`${window.location.origin}${window.location.pathname}#/accounts/${account?.id}/blog-settings`}
							target='_blank'
							rel='noreferrer'
						>
							<span>Manage Settings</span>
						</a>
					),
					type: SettingType.Custom,
				}}
				styles={[styleSheet.subRow]}
				key={`${SettingType.Custom}-upload-rss`}
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Surveys,
				onToggle: onEnableSurveysChanged,
				type: SettingType.Toggle,
				value: account?.features?.surveys?.enabled,
			}}
			key={SettingName.Surveys}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				inputProps: {
					onBlur: onSurveysDefaultReviewInputBlur,
					onChange: onSurveysDefaultReviewInputChanged,
					value: pendingSurveysDefaultReviewUrl,
				},
				label: SettingName.SurveysDefaultReviewLink,
				onToggle: onEnableSurveysChanged,
				type: SettingType.Input,
				value: account?.features?.surveys?.enabled,
			}}
			disabled={!account?.features?.surveys?.enabled}
			key={SettingName.SurveysDefaultReviewLink}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.NewReviewsEmailNotification,
				info: 'If enabled admins and super admins will be notified via email when a new review is posted. They can opt out of this notification in admin section of user settings',
				onToggle: onSetSurveyNewReviewsEmailNotification,
				type: SettingType.Toggle,
				value: account?.features?.surveys?.newReviewsEmailNotification,
			}}
			disabled={!account?.features?.surveys?.enabled}
			key={SettingName.NewReviewsEmailNotification}
			styles={[styleSheet.subRow]}
		/>,
		account?.features?.meetingScheduler?.enabled ||
		account?.features?.surveys?.enabled ||
		account?.features?.blogFeature?.enabled ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: (
						<a
							className={css(baseStyleSheet.brandLink)}
							href='#'
							onClick={e => {
								e.preventDefault();
								onManageCustomBrandingClick();
							}}
						>
							<span>Template Designer</span>
						</a>
					),
					info: 'Customize the header and footer of your meetings, surveys, and blogs.',
					type: SettingType.Custom,
				}}
				key='ManageCustomBranding'
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.DefaultEmailStyle,
				info: 'Set default font styles for all email templates. When enabled, new and existing emails will use these font settings unless specifically overridden.',
				onToggle: handleDefaultEmailStyleEnabledChanged,
				type: SettingType.Toggle,
				value: pendingDefaultEmailStyleEnabled,
			}}
			key={SettingName.DefaultEmailStyle}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.DefaultEmailFontFace,
				onSelectionChange: handleDefaultEmailFontFaceChanged,
				options: DefaultEmailFontFaceOptions,
				selectedOption: DefaultEmailFontFaceOptions.find(o => o.dataContext === pendingDefaultEmailFontFace),
				type: SettingType.Dropdown,
			}}
			disabled={!pendingDefaultEmailStyleEnabled}
			key={SettingName.DefaultEmailFontFace}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.DefaultEmailFontSize,
				onSelectionChange: handleDefaultEmailFontSizeChanged,
				options: DefaultEmailFontSizeOptions,
				selectedOption: DefaultEmailFontSizeOptions.find(o => o.dataContext === pendingDefaultEmailFontSize),
				type: SettingType.Dropdown,
			}}
			disabled={!pendingDefaultEmailStyleEnabled}
			key={SettingName.DefaultEmailFontSize}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Texting,
				onToggle: onTextingChanged,
				type: SettingType.Toggle,
				value: account?.features?.texting?.enabled,
			}}
			key={SettingName.Texting}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.TextingArchivingEnabled,
				info: 'If enabled, all text messages will be archived in the account to satisfy compliance needs. By default, this is enabled for Financial accounts. Please open a support ticket to change as needed.',
				onToggle: onTextingRequireArchivingChanged,
				type: SettingType.Toggle,
				value: account?.features?.texting?.requiresArchiving,
				settingDisabled: !(hasDevPermission || hasSupportPermission),
			}}
			disabled={!account?.features?.texting?.enabled}
			key={SettingName.TextingArchivingEnabled}
			styles={[styleSheet.subRow]}
		/>,
		account?.mainAccountId &&
		account?.planDetails?.billingType === 'comp' &&
		(hasDevPermission || hasSupportPermission) ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: SettingName.UseParentTextingCampaignRegistration,
					info: 'This account must be linked to a main account to use parent campaign registration. Once enabled, this cannot be disabled.',
					onToggle: onTextingUseParentCampaignRegistrationChanged,
					type: SettingType.Toggle,
					value: account?.features?.texting?.useParentTextingCampaignRegistration,
					settingDisabled: account?.features?.texting?.useParentTextingCampaignRegistration, // Disables toggle when true
				}}
				disabled={!account?.features?.texting?.enabled}
				key={SettingName.UseParentTextingCampaignRegistration}
				styles={[styleSheet.subRow]}
			/>
		) : null,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				inputProps: {
					styles: [styleSheet.numericInput],
					name: SettingName.TextingDailySendLimit,
					onBlur: onTextingDailySendLimitInputBlur,
					onChange: onTextingInputChanged,
					placeholder: textingDailySendLimit?.toString(),
					value: pendingTextingDailySendLimit,
				},
				label: SettingName.TextingDailySendLimit,
				type: SettingType.Input,
			}}
			disabled={!account?.features?.texting?.enabled}
			key={SettingName.TextingDailySendLimit}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				inputProps: {
					styles: [styleSheet.numericInput],
					name: SettingName.TextingHourlySendLimit,
					onBlur: onTextingHourlySendLimitInputBlur,
					onChange: onTextingInputChanged,
					placeholder: textingHourlySendLimit?.toString(),
					value: pendingTextingHourlySendLimit,
				},
				label: SettingName.TextingHourlySendLimit,
				type: SettingType.Input,
			}}
			disabled={!account?.features?.texting?.enabled}
			key={SettingName.TextingHourlySendLimit}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				inputProps: {
					styles: [styleSheet.numericInput],
					name: SettingName.TextingSecondsBetweenSends,
					onBlur: onTextingSecondsBetweenSendsInputBlur,
					onChange: onTextingInputChanged,
					placeholder: textingSecondsBetweenSends?.toString(),
					value: pendingTextingSecondsBetweenSends,
				},
				label: SettingName.TextingSecondsBetweenSends,
				type: SettingType.Input,
			}}
			disabled={!account?.features?.texting?.enabled}
			key={SettingName.TextingSecondsBetweenSends}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: 'Texting Provider',
				onSelectionChange: onTextingProviderChanged,
				options: TextingProviderOptions,
				selectedOption:
					TextingProviderOptions.find(o => o.dataContext === account?.features?.texting?.provider) ??
					TextingProviderOptions.find(o => o.dataContext === ETextingFeatureProvider.Bandwidth),
				type: SettingType.Dropdown,
			}}
			disabled={!account?.features?.texting?.enabled}
			key='Texting Provider'
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={hasDevPermission || hasSupportPermission}
			data={{
				category: SettingCategory.Features,
				label: SettingName.HandwrittenCards,
				onToggle: onHandwrittenCardsEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.handwrittenCards?.enabled,
			}}
			key={SettingName.HandwrittenCards}
		/>,
		account?.features?.handwrittenCards?.enabled ? (
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Features,
					label: 'Card Provider',
					onSelectionChange: onHandwrittenCardsProviderChanged,
					options: HandwrittenCardProviderOptions,
					selectedOption:
						HandwrittenCardProviderOptions.find(o => o.dataContext === account?.features?.handwrittenCards?.provider) ??
						HandwrittenCardProviderOptions.find(o => o.dataContext === HandwrittenCardProvider.SimplyNoted),
					type: SettingType.Dropdown,
				}}
				disabled={!account?.features?.handwrittenCards?.enabled}
				key='Card Provider'
				styles={[styleSheet.subRow]}
			/>
		) : null,

		account?.features?.handwrittenCards?.enabled ? (
			<Setting
				canModifyAccount={hasDevPermission || hasSupportPermission}
				disabled={account.features.handwrittenCards.provider !== HandwrittenCardProvider.Cardly}
				styles={[{ ...styleSheet.infoFieldContainerFullContainer }, styleSheet.subRow]}
				data={{
					category: SettingCategory.Features,
					label: (
						<div className={css(styleSheet.companyLogoButtonWrap)}>
							<button className={css(styleSheet.addCustomCard)} onClick={!canModifyAccount ? undefined : uploadLogo}>
								{SettingName.AddCustomCard}
							</button>
						</div>
					),
					type: SettingType.Custom,
				}}
				key={SettingName.AddCustomCard}
			/>
		) : null,

		...(myHandwrittenCardTemplatesQuery?.data?.map((template, index) => {
			return (
				<Setting
					canModifyAccount={hasDevPermission || hasSupportPermission}
					styles={[{ ...styleSheet.infoFieldContainerFullContainer }, styleSheet.subRow]}
					data={{
						category: SettingCategory.Features,
						label: (
							<div className={css(styleSheet.companyLogoButtonWrap)}>
								<button className={css(styleSheet.addCustomCard)} onClick={() => alert('Editing not yet supported')}>
									{template.name}
								</button>
							</div>
						),
						type: SettingType.Custom,
					}}
					key={`${template.name}-${index}`}
				/>
			);
		}) ?? []),

		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Households,
				onToggle: onHouseholdsEnabledChanged,
				type: SettingType.Toggle,
				value: account?.features?.households?.enabled,
			}}
			key={SettingName.Households}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.HouseholdsSendToHouseholdsByDefault,
				onToggle: onHouseholdsSendToHouseholdByDefaultChanged,
				type: SettingType.Toggle,
				value: account?.features?.households?.sendToHouseholdByDefault,
			}}
			key={SettingName.HouseholdsSendToHouseholdsByDefault}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={hasDevPermission || hasSupportPermission}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Timeline,
				onToggle: onTimelineChanged,
				type: SettingType.Toggle,
				value: account?.features?.timeline?.enabled,
			}}
			key={SettingName.Timeline}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.Translations,
				onToggle: onEnableTranslationChanged,
				type: SettingType.Toggle,
				value: account?.features?.translation?.enabled,
			}}
			key={SettingName.Translations}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.ContentGeneration,
				onToggle: onEnableContentGenerationChanged,
				type: SettingType.Toggle,
				value: account?.features?.contentGeneration?.enabled,
			}}
			key={SettingName.ContentGeneration}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				info: 'This setting should only be used for Global Talent accounts',
				label: SettingName.CrossAccountTemplateSharing,
				onToggle: onCrossAccountTemplateSharingChanged,
				type: SettingType.Toggle,
				value: account?.features?.crossAccountTemplateSharing?.enabled,
			}}
			key={SettingName.CrossAccountTemplateSharing}
		/>,

		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.CustomContentCalendar,
				info: 'Enable the custom content calendar for this account',
				onToggle: onEnableCustomContentCalendarChanged,
				type: SettingType.Toggle,
				value: account?.features?.customContentCalendar?.enabled,
			}}
			key={SettingName.CustomContentCalendar}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.PublicAPI,
				onToggle: onEnablePublicAPIChanged,
				type: SettingType.Toggle,
				value: account?.features?.publicApi?.enabled,
			}}
			key={SettingName.PublicAPI}
		/>,
		<Setting
			isNew
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Features,
				label: SettingName.EnableStripePayments,
				onToggle: onEnableStripePaymentsChanged,
				type: SettingType.Toggle,
				value: account?.features?.stripePayment?.enabled,
			}}
			key={SettingName.EnableStripePayments}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.DisableMobileApps,
				onToggle: onDisableMobileAppsChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.disableMobileApps,
			}}
			key={SettingName.DisableMobileApps}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.HideCompanies,
				onToggle: onHideCompaniesChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.hideCompanies,
			}}
			key={SettingName.HideCompanies}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.HideContactCompanyEmployeeReport,
				onToggle: onHideContactCompanyEmployeeReportChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.hideContactCompanyEmployeeReport,
			}}
			key={SettingName.HideContactCompanyEmployeeReport}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.HideEmailCampaignSignature,
				onToggle: onHideEmailCampaignSignatureChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.hideEmailCampaignSignature,
				info: "If the user's signature is an image, it will be a linked image instead of embedded as an attachment.",
			}}
			key={SettingName.HideEmailCampaignSignature}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: SettingName.ShowAllContactsByDefault,
				onToggle: onShowAllContactsByDefaultChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.showAllContactsByDefault,
			}}
			key={SettingName.ShowAllContactsByDefault}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Will enforce compliance settings for emails, social and blogs',
				label: SettingName.ComplianceEnabled,
				onToggle: onComplianceEnabledChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.complianceSettings?.enabled,
			}}
			key={SettingName.ComplianceEnabled}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Minimum wait time before Compliance emails can be sent - allowing for notification to the compliance officer.',
				label: SettingName.WaitTimeForCompliance,
				onSelectionChange: onComplianceWaitTimeChanged,
				options: SendOnBehalfWaitTimeOptions,
				selectedOption: getComplianceWaitTimeOption(),
				type: SettingType.Dropdown,
			}}
			disabled={!account?.preferences?.complianceSettings?.enabled}
			key={SettingName.WaitTimeForCompliance}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: "When enabled, compliance officers receive confirmation emails with PDF attachments of the content after approving or denying. These emails can serve as archival records for compliance regulations (e.g., FINRA) and can be retained in the recipient's email systems.",
				label: SettingName.SendComplianceReviewResultEmails,
				onToggle: onSendComplianceReviewResultEmailsChanged,
				type: SettingType.Toggle,
				value: account?.preferences?.complianceSettings?.sendComplianceReviewResultEmails,
			}}
			disabled={!account?.preferences?.complianceSettings?.enabled}
			key={SettingName.SendComplianceReviewResultEmails}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Enable UI to see duplicate contacts. Useful for customers importing with AdditionalId in their CSVs, using Zapier or configured integrations',
				label: SettingName.ShowDuplicateContacts,
				onToggle: checked => onToggleAccountPreferenceBoolean('showDuplicateContacts', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.showDuplicateContacts,
			}}
			key={SettingName.ShowDuplicateContacts}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'When enabled, manually created contacts will automatically merge into matching integration contacts based on email address. The integration contact is always preserved.',
				label: SettingName.AllowEmailMergeIntoIntegration,
				onToggle: checked => onToggleAccountPreferenceBoolean('allowEmailMergeIntoIntegration', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.allowEmailMergeIntoIntegration,
			}}
			key={SettingName.AllowEmailMergeIntoIntegration}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Allow users to change emails',
				label: SettingName.AllowChangeUserEmail,
				onToggle: checked => onToggleAccountPreferenceBoolean('allowChangeUserEmail', checked),
				type: SettingType.Toggle,
				value: account?.preferences?.allowChangeUserEmail,
			}}
			key={SettingName.AllowChangeUserEmail}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				label: <span>{SettingName.CustomFields}</span>,
				type: SettingType.Custom,
				value: true,
			}}
			key={SettingName.CustomFields}
		/>,
		account.additionalInfo?.hasCustomContactFields ? (
			<Setting
				onCustomCallback={editCustomFields}
				customText='Edit'
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Administrative,
					label: (
						<div className={css(styleSheet.customFieldsList)}>
							<span>
								{customFields.length ? customFields.map(field => field.label).join(', ') : '(No custom fields)'}
							</span>
						</div>
					),
					type: SettingType.Custom,
				}}
				styles={[styleSheet.subRow]}
				key='custom-field-list'
			/>
		) : (
			<Setting
				canModifyAccount={hasDevPermission || hasSupportPermission}
				styles={[{ ...styleSheet.infoFieldContainerFullContainer }, styleSheet.subRow]}
				data={{
					category: SettingCategory.Administrative,
					label: (
						<div className={css(styleSheet.companyLogoButtonWrap)}>
							<button
								className={css(styleSheet.addCustomCard)}
								disabled={!canModifyAccount}
								onClick={createCustomFieldClicked}
							>
								{SettingName.AddCustomField}
							</button>
						</div>
					),
					type: SettingType.Custom,
				}}
				key={SettingName.AddCustomField}
			/>
		),
		<Setting<UserRole>
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Administrative,
				info: 'Users must have this role or higher to view all of Levitate. Otherwise, they will only be able to manage settings.',
				label: SettingName.RequiredRoleForFullLevitateUI,
				onSelectionChange: onRequiredRoleForFullLevitateUI,
				options: UserOptions,
				selectedOption:
					UserOptions.find(o => o.dataContext === account?.preferences?.requiredRoleForFullLevitateUI) ??
					UserOptions.find(o => o.dataContext === UserRole.LimitedUser),
				type: SettingType.Dropdown,
			}}
			key={SettingName.RequiredRoleForFullLevitateUI}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preReviewed?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: SettingName.PreApproved,
				onToggle: onUpdatePreApprovedFeature,
				type: SettingType.Toggle,
				value: account?.features?.templateReviewSettings?.preApproved?.enabled,
			}}
			key={SettingName.PreApproved}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preReviewed?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Pre-Approved Display Text',
				inputProps: {
					onBlur: e => onUpdatePreApprovedDisplayText?.(e.target.value),
					onChange: onPreApprovedDisplayTextChanged,
					value: pendingPreApprovedDisplayText,
				},
				type: SettingType.Input,
			}}
			disabled={!account?.features?.templateReviewSettings?.preApproved?.enabled}
			key='PreApprovedDisplayText'
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preReviewed?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Pre-Approved Email Prefix',
				inputProps: {
					onBlur: e => onUpdatePreApprovedEmailPrefix?.(e.target.value),
					onChange: onPreApprovedEmailPrefixChanged,
					value: pendingPreApprovedEmailPrefix,
				},
				type: SettingType.Input,
			}}
			disabled={!account?.features?.templateReviewSettings?.preApproved?.enabled}
			key='PreApprovedEmailPrefix'
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preApproved?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: SettingName.PreReviewed,
				onToggle: onUpdatePreReviewedFeature,
				type: SettingType.Toggle,
				value: account?.features?.templateReviewSettings?.preReviewed?.enabled,
			}}
			key={SettingName.PreReviewed}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preApproved?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Pre-Reviewed Display Text',
				inputProps: {
					onBlur: e => onUpdatePreReviewedDisplayText?.(e.target.value),
					onChange: onPreReviewedDisplayTextChanged,
					value: pendingPreReviewedDisplayText,
				},
				type: SettingType.Input,
			}}
			disabled={!account?.features?.templateReviewSettings?.preReviewed?.enabled}
			key='PreReviewedDisplayText'
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount && !account?.features?.templateReviewSettings?.preApproved?.enabled}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Pre-Reviewed Email Prefix',
				inputProps: {
					onBlur: e => onUpdatePreReviewedEmailPrefix?.(e.target.value),
					onChange: onPreReviewedEmailPrefixChanged,
					value: pendingPreReviewedEmailPrefix,
				},
				type: SettingType.Input,
			}}
			disabled={!account?.features?.templateReviewSettings?.preReviewed?.enabled}
			key='PreReviewedEmailPrefix'
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: SettingName.Rejected,
				onToggle: onUpdateRejectedFeature,
				type: SettingType.Toggle,
				value: account?.features?.templateReviewSettings?.rejected?.enabled,
			}}
			key={SettingName.Rejected}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Rejected Display Text',
				inputProps: {
					onBlur: e => onUpdateRejectedDisplayText?.(e.target.value),
					onChange: onRejectedDisplayTextChanged,
					value: pendingRejectedDisplayText,
				},
				type: SettingType.Input,
			}}
			key='RejectedDisplayText'
			disabled={!account?.features?.templateReviewSettings?.rejected?.enabled}
			styles={[styleSheet.subRow]}
		/>,
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.PreApprovedContent,
				label: 'Rejected Email Prefix',
				inputProps: {
					onBlur: e => onUpdateRejectedEmailPrefix?.(e.target.value),
					onChange: onRejectedEmailPrefixChanged,
					value: pendingRejectedEmailPrefix,
				},
				type: SettingType.Input,
			}}
			key='RejectedEmailPrefix'
			disabled={!account?.features?.templateReviewSettings?.rejected?.enabled}
			styles={[styleSheet.subRow]}
		/>,
	].filter(x => !!x);

	if (account?.additionalInfo?.industry === Industry.Insurance) {
		settings.push(
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'How many months to look ahead for "Renewal" emails. The time period begins at the start of the next month, and includes anyone renewing through the selected period.',
					label: SettingName.ShowRenewalDashboardCards,
					onSelectionChange: onSetRenewalReminderDaysOption,
					onToggle: onSetRenewalReminderDaysOptionChange,
					options: RenewalDaysOptions,
					selectedOption: getRenewalCardsOption(),
					type: SettingType.Multi,
					value: !(
						account?.preferences?.renewalEmailDaysToLookAhead === 0 ||
						account?.preferences?.renewalEmailDaysToLookAhead === undefined
					),
				}}
				disabled={!(account?.additionalInfo?.industry === Industry.Insurance)}
				key={SettingName.ShowRenewalDashboardCards}
			/>,
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will suppress any other renewals (even for different policies) through the selected period. Enable this to stop a renewal reminder from appearing again for a given contact.',
					label: SettingName.SuppressContactsRemovedFromRenewal,
					onSelectionChange: onSetRenewalSuppressMonthsOption,
					onToggle: onSetRenewalSuppressMonthsOptionChange,
					options: RenewalMonthsOptions,
					selectedOption: getRenewalSuppressOption(),
					type: SettingType.Multi,
					value: !(
						account?.preferences?.renewalEmailMonthsToSuppress === 0 ||
						account?.preferences?.renewalEmailMonthsToSuppress === undefined
					),
				}}
				disabled={!(account?.additionalInfo?.industry === Industry.Insurance)}
				key={SettingName.SuppressContactsRemovedFromRenewal}
			/>
		);
	}

	if (account?.additionalInfo?.industry === Industry.Financial) {
		settings.push(
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: SettingName.ShowFinancialReviewDashboardCards,
					label: SettingName.ShowFinancialReviewDashboardCards,
					onSelectionChange: onSetFinancialReviewOption,
					onToggle: onSetFinancialReviewOptionChange,
					options: RenewalDaysOptions,
					selectedOption: getFinancialReviewOption(),
					type: SettingType.Multi,
					value: !(
						account?.preferences?.resourceSelectorSettings?.FinancialReview?.daysToLookAhead === 0 ||
						account?.preferences?.resourceSelectorSettings?.FinancialReview?.daysToLookAhead === undefined
					),
				}}
				key={SettingName.ShowFinancialReviewDashboardCards}
			/>,
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show any customers who have their 72nd birthday in the next 60, 90, or 120 days (from the end of the current month). Disabled by default.',
					label: SettingName.ShowTurning72DashboardCards,
					onSelectionChange: onSetTurning72MonthsOption,
					onToggle: onSetTurning72MonthsOptionChange,
					options: Turning72Options,
					selectedOption: getTurning72CardsOption(),
					type: SettingType.Multi,
					value: !(
						account?.preferences?.resourceSelectorSettings?.Turning72?.daysToLookAhead === 0 ||
						account?.preferences?.resourceSelectorSettings?.Turning72?.daysToLookAhead === undefined
					),
				}}
				key={SettingName.ShowTurning72DashboardCards}
			/>,
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show any customers who have their 73rd birthday in the next 60, 90, or 120 days (from the end of the current month). Disabled by default.',
					label: SettingName.ShowTurning73DashboardCards,
					onSelectionChange: onSetTurning73MonthsOption,
					onToggle: onSetTurning73MonthsOptionChange,
					options: Turning73Options,
					selectedOption: getTurning73CardsOption(),
					type: SettingType.Multi,
					value: !(
						account?.preferences?.resourceSelectorSettings?.Turning73?.daysToLookAhead === 0 ||
						account?.preferences?.resourceSelectorSettings?.Turning73?.daysToLookAhead === undefined
					),
				}}
				key={SettingName.ShowTurning73DashboardCards}
			/>
		);
	}

	if (account?.features.boards?.donationsEnabled) {
		settings.push(
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show new donations that were added in the past 14 days. Donations will show for 60 days. Disabled by default during rollout period.',
					label: SettingName.ShowNewDonationsDashboardCard,
					onToggle: onSetNewDonationsOptionChange,
					type: SettingType.Toggle,
					value: (account?.preferences?.resourceSelectorSettings?.NewDonations as IActionableResourceSelectorSettings)
						?.enabled,
				}}
				key={SettingName.ShowNewDonationsDashboardCard}
			/>
		);
	}

	settings.push(
		<Setting
			canModifyAccount={canModifyAccount}
			data={{
				category: SettingCategory.Dashboard,
				info: 'Will show new policies that were added to contacts. Policies will be actionable for 60 days and kept for 90 days.',
				label: SettingName.ShowNewPolicyDashboardCard,
				onToggle: onSetNewPolicyOptionChange,
				type: SettingType.Toggle,
				value: (
					account?.preferences?.resourceSelectorSettings?.NewPolicy as INewPolicyActionableResourceSelectorSettings
				)?.enabled,
			}}
			key={SettingName.ShowNewPolicyDashboardCard}
		/>
	);

	if (account?.additionalInfo?.industry === Industry.Legal) {
		settings.push(
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show new matters that were added. Matters will be actionable for 60 days.',
					label: SettingName.ShowNewMatterDashboardCard,
					onToggle: onSetNewMatterOptionChange,
					type: SettingType.Toggle,
					value: (account?.preferences?.resourceSelectorSettings?.NewMatter as IActionableResourceSelectorSettings)
						?.enabled,
				}}
				key={SettingName.ShowNewMatterDashboardCard}
			/>,
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show matters where the stage has changed.',
					label: SettingName.ShowMatterStageChangedDashboardCard,
					onToggle: onSetMatterStageChangedOptionChange,
					type: SettingType.Toggle,
					value: (
						account?.preferences?.resourceSelectorSettings?.MatterStageChanged as IActionableResourceSelectorSettings
					)?.enabled,
				}}
				key={SettingName.ShowMatterStageChangedDashboardCard}
			/>,
			<Setting
				canModifyAccount={canModifyAccount}
				data={{
					category: SettingCategory.Dashboard,
					info: 'Will show matters where the status has changed.',
					label: SettingName.ShowMatterStatusChangedDashboardCard,
					onToggle: onSetMatterStatusChangedOptionChange,
					type: SettingType.Toggle,
					value: (
						account?.preferences?.resourceSelectorSettings?.MatterStatusChanged as IActionableResourceSelectorSettings
					)?.enabled,
				}}
				key={SettingName.ShowMatterStatusChangedDashboardCard}
			/>
		);
	}

	settings = settings.filter(Boolean);

	customCards.forEach(card => {
		if (account?.preferences?.resourceSelectorSettings?.[card]) {
			settings.push(getCustomCard(card));
		}
	});

	if (customCards?.length < 10) {
		settings.push(
			<Setting
				canModifyAccount={canModifyAccount}
				styles={[{ ...styleSheet.infoFieldContainerFullContainer }]}
				data={{
					category: SettingCategory.Dashboard,
					label: (
						<button className={css(styleSheet.addCustomCard)} onClick={!canModifyAccount ? undefined : onShowModal}>
							{SettingName.AddCustomDashboardCard}
						</button>
					),
					type: SettingType.Custom,
				}}
				key={SettingName.AddCustomDashboardCard}
			/>
		);
	}

	return (
		<div className={css(styleSheet.column)}>
			<div className={css(styleSheet.header)}>{category.toUpperCase()}</div>
			{settings.filter((s, i) => {
				if (s?.props?.data?.category == null) {
					console.log(`null setting found at index ${i}`);
					return false;
				}
				let include = s.props.data.category === category;

				if (include && settingsSearch.length > 0) {
					const searchRegExp = new RegExp(settingsSearch, 'i');

					include =
						s.props.data.label && typeof s.props.data.label === 'string'
							? searchRegExp.test(s.props.data.label) || searchRegExp.test(s.props.data.info)
							: s.props.data.info
								? searchRegExp.test(s.props.data.info)
								: s.props.data.label?.type === 'button'
									? searchRegExp.test(s.props.data.label?.props?.children)
									: false;
				}

				return include;
			})}

			{showAddLogoModal ? <AddCompanyLogoModal account={account} onRequestClose={onRequestClose} /> : null}

			{showCreateCustomFieldsModal ? (
				<CreateCustomFieldsModal
					initialFields={customFields}
					onSave={onCustomFieldSave}
					onCancel={onCustomFieldCancel}
					impersonationContext={impersonationContext}
				/>
			) : null}

			<CreateCustomDashboardCardModal
				card={editCustomCard ? account?.preferences.resourceSelectorSettings[editCustomCard] : null}
				cardType={editCustomCard}
				onSubmittedValues={handleSubmittedCustomCard}
				modalProps={{
					isOpen: showCreateCustomDashboardCardModal,
					onRequestClose: onModalRequestClose,
				}}
			/>
			<ConfirmationDialog
				className={css(styleSheet.deleteRenewKeyFactsModal)}
				icon={<WarningIcon />}
				modalProps={{
					isOpen: confirmDisable,
					onRequestClose: result => {
						if (result.isDestructive) {
							onSuppressCustomCardOptionChange(editCustomCard);
						}
						setTimeout(() => {
							setEditCustomCard(null);
						}, 500);

						setConfirmDisable(false);
					},
				}}
				options={DeleteKeyFactsConfirmationOptions}
				title={`Are you sure you want to delete the "${
					editCustomCard ? account?.preferences?.resourceSelectorSettings[editCustomCard]?.displayName : ''
				} Dashboard Card" for this account? This operation cannot be undone.`}
			/>
		</div>
	);
};
